import { t } from "i18next";
import { RefreshCcw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { AppConfig } from "@/common/appConfig";
import { AUDIO_TRANSCRIPTION_NO_REALTIME_PROVIDERS } from "@/common/config";
import { Button } from "@/components/ui/button";
import ConfirmDialog from "@/components/ui/ConfirmDialog";
import { SplitSelectButton } from "@/components/ui/SplitSelectButton";
import { TranscriptionNoRealtimeProviderName } from "@/hooks/transcriber/types";
import { toast } from "@/hooks/use-toast";
import { useRegenerateTranscription } from "@/hooks/visitsTemplates/useRegenerateTranscription";
import { useUpdateVisitTemplate } from "@/hooks/visitsTemplates/useUpdateVisitTemplate";
import { cn } from "@/lib/utils";
import type { FileData } from "@/services/files";

// Save and regenerate behaviour of the main visit note. Shared by the classic
// note card and the redesigned workspace so both keep the same API calls,
// recovery handling and toasts.
export const useMainNoteActions = (file: FileData) => {
  const { updateTemplate, result, loading, error } = useUpdateVisitTemplate();
  const quiet = useRef(false);
  const regeneration = useRegenerateTranscription(file.public_id);
  const [providerSelected, setProviderSelected] = useState<string>(
    TranscriptionNoRealtimeProviderName.DEEPGRAM_NOVA_3_MEDICAL,
  );

  useEffect(() => {
    if (!loading) {
      if (error) {
        toast({
          variant: "error",
          title: t("visit.messages.updateFailed"),
        });
      }
      // A save that asked to stay quiet (the review card advances on its
      // own) shows no success toast; failures always do.
      if (result && !quiet.current) {
        toast({
          variant: "success",
          // v3.2: a short "Saved" pill.
          title: AppConfig.enableAppRedesign
            ? t("v3.toast.saved")
            : t("visit.messages.updateSuccessful"),
        });
      }
    }
  }, [error, loading, result]);

  // True when saved (see useVisitWrites).
  const save = (
    value: string,
    options: { quiet?: boolean } = {},
  ): Promise<boolean> => {
    quiet.current = !!options.quiet;
    return updateTemplate({ result: value, public_id: file.public_id });
  };

  const regenerate = (): void => {
    void regeneration.regenerate({
      visitPublicId: file.public_id,
      audioTranscriptionProviderId:
        regeneration.savedProvider ?? providerSelected,
    });
  };

  return {
    save,
    saving: loading,
    busy: regeneration.loading || loading,
    regenerate,
    regeneration,
    providerSelected,
    setProviderSelected,
    // Regenerate is hidden unless VITE_NOTE_REGENERATE_ENABLED is on.
    canRegenerate: AppConfig.enableNoteRegenerate && !!file.name,
  };
};

export type MainNoteActions = ReturnType<typeof useMainNoteActions>;

interface Props {
  actions: MainNoteActions;
  // Classic card: small default button. Workspace: outline, toolbar sized.
  variant?: "classic" | "workspace";
  className?: string;
}

// Transcription recovery notices (retry or abandon a saved request) and the
// result of the last regeneration.
export const MainNoteNotices = ({
  actions,
}: {
  actions: MainNoteActions;
}): React.ReactNode => {
  const [confirmAbandon, setConfirmAbandon] = useState(false);
  const {
    loading: regenerating,
    error: transcriptionError,
    recovery,
    retry,
    abandon,
    result: transcriptionResult,
  } = actions.regeneration;

  return (
    <>
      {(transcriptionError || recovery) && (
        <div role="alert" className="space-y-2">
          <p>
            {transcriptionError ?? t("visit.recovery.transcriptionDescription")}
          </p>
          {recovery && (
            <>
              <Button
                disabled={regenerating}
                variant="outline"
                onClick={() => void retry()}
              >
                {t("visit.recovery.retrySaved")}
              </Button>
              <ConfirmDialog
                open={confirmAbandon}
                onOpenChange={setConfirmAbandon}
                title={t("visit.recovery.abandonTranscriptionTitle")}
                description={t(
                  "visit.recovery.abandonTranscriptionDescription",
                )}
                trigger={
                  <Button disabled={regenerating} variant="outline">
                    {t("visit.recovery.abandonSaved")}
                  </Button>
                }
                onConfirm={abandon}
              />
            </>
          )}
        </div>
      )}
      {transcriptionResult === "notes" && (
        <p role="status">{t("visit.recovery.regeneratedFromTranscript")}</p>
      )}
    </>
  );
};

// Transcription recovery notices and the Regenerate split button.
export const MainNoteTools = ({
  actions,
  variant = "classic",
  className,
}: Props): React.ReactNode => {
  const {
    loading: regenerating,
    recovery,
    savedProvider,
  } = actions.regeneration;

  return (
    <div className={cn("space-y-2", className)}>
      <MainNoteNotices actions={actions} />
      {actions.canRegenerate && (
        <SplitSelectButton
          className={
            variant === "workspace" ? "h-11 w-full sm:w-auto lg:h-9" : undefined
          }
          disabled={actions.busy || recovery}
          options={AUDIO_TRANSCRIPTION_NO_REALTIME_PROVIDERS}
          size={variant === "workspace" ? "default" : "sm"}
          value={savedProvider ?? actions.providerSelected}
          variant={variant === "workspace" ? "outline" : "default"}
          onClick={actions.regenerate}
          onValueChange={actions.setProviderSelected}
        >
          <RefreshCcw className={cn(regenerating && "animate-spin")} />
          {t("visits.templateViewer.buttons.regenerate")}
        </SplitSelectButton>
      )}
    </div>
  );
};
